/**
 * Same-origin fetch wrapper with auto-refresh.
 *
 * v1-final auth model:
 *   - Access token lives in module-level memory (never localStorage / sessionStorage)
 *   - Refresh token lives in an HttpOnly cookie (browser handles it, JS can't read)
 *   - On 401 from any /api/* call, this module calls /api/auth/refresh once,
 *     refreshes the access token, and retries the original request. If the
 *     refresh itself returns 401, the user is fully logged out.
 *   - All requests use `credentials: "include"` so the refresh cookie is
 *     sent on calls to /api/auth/refresh (its Path scope).
 *
 * Refresh is deduplicated — concurrent 401s share one in-flight refresh
 * promise. Without dedup, every concurrent request would trigger its own
 * refresh + race for the next rotated cookie.
 */
import type { ApiError, ApiSuccess } from "../types";

// ----------------------------------------------------------------------------
// In-memory access token store
// ----------------------------------------------------------------------------

let accessToken: string | null = null;
type Listener = (token: string | null) => void;
const listeners = new Set<Listener>();

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  for (const l of listeners) l(token);
}

/** Subscribe to access-token changes (used by useAuth to react to refresh/logout). */
export function onAccessTokenChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

// ----------------------------------------------------------------------------
// API call types
// ----------------------------------------------------------------------------

export type ApiResult<T = unknown> =
  | { ok: true; data: ApiSuccess<T> }
  | { ok: false; error: ApiError; status: number };

interface RequestOpts {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Set false on public endpoints that don't need Authorization header. */
  auth?: boolean;
  /** Internal — prevents infinite refresh loops. */
  _skipRefresh?: boolean;
  signal?: AbortSignal;
}

// ----------------------------------------------------------------------------
// Refresh dedup: one in-flight refresh shared by all callers
// ----------------------------------------------------------------------------

let refreshInFlight: Promise<boolean> | null = null;

async function attemptRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const res = await rawCall<{ token: string }>("/api/auth/refresh", {
      method: "POST",
      auth: false,
      _skipRefresh: true,
    });
    if (res.ok && res.data.token) {
      setAccessToken(res.data.token);
      return true;
    }
    setAccessToken(null);
    return false;
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

/** Public: run an eager refresh on app mount to restore session from cookie. */
export async function tryEagerRefresh(): Promise<boolean> {
  return attemptRefresh();
}

/** Public: server-side logout — bumps refreshTokenVersion + clears cookie. */
export async function serverLogout(): Promise<void> {
  if (!accessToken) return;
  await rawCall("/api/auth/logout", { method: "POST", _skipRefresh: true });
  setAccessToken(null);
}

// ----------------------------------------------------------------------------
// rawCall — actually performs the fetch. apiCall wraps it with refresh logic.
// ----------------------------------------------------------------------------

async function rawCall<T = unknown>(path: string, opts: RequestOpts = {}): Promise<ApiResult<T>> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (opts.auth !== false && accessToken) headers.Authorization = `Bearer ${accessToken}`;

  let res: Response;
  try {
    res = await fetch(path, {
      method: opts.method ?? "GET",
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      credentials: "include",  // sends the refresh cookie on /api/auth/refresh calls
      signal: opts.signal,
    });
  } catch (err) {
    return {
      ok: false,
      status: 0,
      error: {
        success: false,
        message: err instanceof Error ? err.message : "Network error",
      },
    };
  }

  if (res.status === 204) return { ok: true, data: { success: true } as ApiSuccess<T> };

  let body: unknown;
  try { body = await res.json(); }
  catch {
    return {
      ok: false,
      status: res.status,
      error: { success: false, message: `HTTP ${res.status}: invalid JSON response` },
    };
  }

  if (res.ok && typeof body === "object" && body !== null && (body as { success?: unknown }).success === true) {
    return { ok: true, data: body as ApiSuccess<T> };
  }
  return {
    ok: false,
    status: res.status,
    error:
      typeof body === "object" && body !== null
        ? (body as ApiError)
        : { success: false, message: `HTTP ${res.status}` },
  };
}

// ----------------------------------------------------------------------------
// apiCall — public wrapper with auto-refresh
// ----------------------------------------------------------------------------

export async function apiCall<T = unknown>(path: string, opts: RequestOpts = {}): Promise<ApiResult<T>> {
  const first = await rawCall<T>(path, opts);

  // Only attempt refresh on 401, and only if we weren't already inside a
  // refresh attempt (the _skipRefresh flag), and only if the caller is using
  // auth (no point refreshing on public endpoints).
  if (
    !first.ok &&
    first.status === 401 &&
    !opts._skipRefresh &&
    opts.auth !== false
  ) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      return rawCall<T>(path, { ...opts, _skipRefresh: true });
    }
  }
  return first;
}
